import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import {
  Row, Col, Button, Card, CardBody, CardTitle, CardSubtitle,
  Form, FormGroup, Label, Input, Alert, Badge, CardFooter
} from "reactstrap";
import { TailSpin } from "react-loader-spinner";
import { useHistory } from "react-router-dom";
import moment from "moment";

import AviatorFrame from "components/AviatorFrame/AviatorFrame";
import OnlineUsers from "components/OnlineUsers/OnlineUsers";
import FloatingClock from "components/FloatingClock/FloatingClock";
import { Enviroments } from "../enviroments/enviroments";
import { cactusApi } from "../services/cactusApi";

const COLORS = {
  bg: "#0B0B0F",
  surface: "#121317",
  border: "#26272B",
  text: "#ECECF3",
  sub: "#A3A6AF",
  primary: "#913ef8",
  pink: "#e14eca",
  yellow: "#F6E05E",
  low: "#8B8E98",
  blue: "rgb(52, 180, 255)"
};

/* ---------- Shimmer (Skeleton) ---------- */
const Shimmer = ({ width = "100%", height = 18, style = {} }) => (
  <div
    className="shimmer"
    style={{
      width,
      height,
      borderRadius: 6,
      background:
        "linear-gradient(90deg, rgba(255,255,255,0.06) 25%, rgba(255,255,255,0.12) 37%, rgba(255,255,255,0.06) 63%)",
      backgroundSize: "400% 100%",
      animation: "shimmer 1.2s ease-in-out infinite",
      ...style
    }}
  />
);

/* ---------- Chips de multiplicadores ---------- */
const getChipStyle = (odd) => {
  const v = parseFloat(odd);
  if (Number.isNaN(v)) {
    return { bg: COLORS.surface, text: COLORS.sub, border: COLORS.border };
  }
  if (v >= 10) {
    return { bg: "rgba(225,78,202,0.16)", text: COLORS.pink, border: COLORS.pink }; // 10x+
  }
  if (v >= 2) {
    return { bg: "rgba(145,62,248,0.14)", text: COLORS.primary, border: COLORS.primary }; // 3–5x
  }
  if (v < 2) {
    return { bg: "#1b1c21", text: COLORS.blue, border: COLORS.blue }; // 2–3x
  }
  return { bg: "#16171b", text: COLORS.low, border: COLORS.border }; // <2x
};

/* ---------- Tarja de últimos multiplicadores ---------- */
const MultipliersStrip = ({ data, loading, containerRef }) => {
  return (
    <Card style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}` }} className="mb-3">
      <CardBody className="py-3">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <span style={{ color: COLORS.sub, fontSize: 12 }}>Últimos multiplicadores</span>
          <span style={{ color: COLORS.sub, fontSize: 11 }}>(← mais recente)</span>
        </div>

        {loading ? (
          <div style={{ display: "flex", gap: 8, overflow: "hidden" }}>
            {Array.from({ length: 10 }).map((_, i) => (
              <Shimmer key={i} width={60} height={28} />
            ))}
          </div>
        ) : data && data.length ? (
          <div
            ref={containerRef}
            style={{
              display: "flex",
              gap: 8,
              overflowX: "auto",
              paddingBottom: 6
            }}
          >
            {data.map((item, idx) => {
              const style = getChipStyle(item.odd);
              return (
                <div
                  key={`${item.id}-${item.hour}-${idx}`}
                  title={`${item.hour} • ${item.odd}x`}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 14,
                    border: `1px solid ${style.border}`,
                    background: style.bg,
                    color: style.text,
                    fontWeight: 700,
                    minWidth: 58,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    fontSize: 13
                  }}
                >
                  {Number(item.odd || 0).toFixed(2)}x
                </div>
              );
            })}
          </div>
        ) : (
          <span style={{ color: COLORS.sub, fontSize: 13 }}>—</span>
        )}
      </CardBody>
    </Card>
  );
};

/* ---------- Cards de status ---------- */
const StatCard = ({ title, accent, nextTime, intervalText, loading }) => (
  <Card className="mb-3" style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
    <CardBody className="py-3">
      <CardTitle tag="h6" className="mb-1" style={{ color: COLORS.sub }}>{title}</CardTitle>
      <CardSubtitle className="mb-1" style={{ color: COLORS.sub, fontSize: 12 }}>
        Próximo horário estimado
      </CardSubtitle>

      {/* Próximo horário */}
      <div style={{ minHeight: 34 }}>
        {loading ? (
          <Shimmer width={140} height={28} />
        ) : (
          <div style={{ fontSize: 28, fontWeight: 800, color: accent }}>
            {nextTime || "N/A"}
          </div>
        )}
      </div>

      {/* Frase do intervalo médio */}
      <div className="mt-2" style={{ color: COLORS.sub, fontSize: 13, minHeight: 22 }}>
        {loading ? (
          <Shimmer width={220} height={16} />
        ) : intervalText ? (
          <Badge pill style={{ background: COLORS.bg, color: COLORS.sub, border: `1px solid ${COLORS.border}` }}>
            {intervalText}
          </Badge>
        ) : (
          "—"
        )}
      </div>
    </CardBody>
  </Card>
);

/* ---------- Cabeçalho ---------- */
const HeaderBar = ({ onBack }) => (
  <Row className="align-items-center mb-3" style={{ position: "sticky", top: 0, zIndex: 2, background: "transparent", paddingTop: 8 }}>
    <Col xs="6" md="6" className="d-flex align-items-center">
      <Button color="link" onClick={onBack} className="p-0 text-left" style={{ color: COLORS.text }}>
        <i className="fas fa-arrow-left mr-2" /> voltar
      </Button>
    </Col>
    <Col xs="6" md="6" className="text-right">
      <h4 className="m-0" style={{ color: COLORS.text, fontWeight: 700 }}>I.A. PRO</h4>
    </Col>
    <Col xs="12" className="d-flex justify-content-center mt-2">
      <OnlineUsers />
    </Col>
  </Row>
);

const AviatorPRO = () => {
  const history = useHistory();
  const dateNow = useMemo(() => moment(new Date()).format("YYYY-MM-DD"), []);
  const [historico, setHistorico] = useState([]);
  const [sinal, setSinal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [urlAviator, setUrlAviator] = useState("");
  const [numberVelas, setNumberVelas] = useState(100);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [showFloatingClock, setShowFloatingClock] = useState(true);
  const intervalRef = useRef(null);

  // ref para auto-scroll da tarja
  const stripRef = useRef(null);

  const handleIframeUrl = () =>
    urlAviator === "" ? "https://esportiva.bet.br" : urlAviator;

  /* ---------- Análise: intervalo médio e próximo horário ---------- */
  const analisarHistorico = (dados) => {
    if (!dados || dados.length === 0) {
      return {
        proximoHorario3X: "N/A",
        intervaloMedio3X: "N/A",
        proximoHorario10X: "N/A",
        intervaloMedio10X: "N/A",
      };
    }

    const toMinutes = (hms) => {
      const [h, m, s] = hms.split(":").map(Number);
      return h * 60 + m + s / 60;
    };

    const fmtClock = (min) => {
      const t = min % 1440;
      const hh = Math.floor(t / 60);
      const mm = Math.floor(t % 60);
      const ss = Math.round((t % 1) * 60);
      return `${`${hh}`.padStart(2, "0")}:${`${mm}`.padStart(2, "0")}:${`${ss}`.padStart(2, "0")}`;
    };

    const fmtInterval = (min) => {
      const totalSec = Math.round(min * 60);
      const mm = Math.floor(totalSec / 60);
      const ss = totalSec % 60;
      return ss ? `${mm}m ${ss}s` : `${mm}m`;
    };

    const calcular = (limiar) => {
      const arr = (dados || [])
        .filter((i) => parseFloat(i.odd) > limiar)
        .sort((a, b) => a.hour.localeCompare(b.hour));

      if (arr.length < 2) {
        return { proximoHorario: "N/A", intervaloMedioFmt: "N/A" };
      }

      const horarios = arr.map((i) => toMinutes(i.hour));
      const intervalos = horarios
        .map((v, i) => (i === 0 ? 0 : v - horarios[i - 1]))
        .filter((v) => v > 0);

      const intervaloMedio = intervalos.reduce((a, b) => a + b, 0) / intervalos.length;

      // próximo horário estimado (último + intervalo médio, avançando até > agora)
      const now = toMinutes(moment().format("HH:mm:ss"));
      let prox = horarios[horarios.length - 1] + intervaloMedio;
      while (prox <= now) prox += intervaloMedio;

      return {
        proximoHorario: fmtClock(prox),
        intervaloMedioFmt: fmtInterval(intervaloMedio),
      };
    };

    const r3 = calcular(3);
    const r10 = calcular(10);

    return {
      proximoHorario3X: r3.proximoHorario,
      intervaloMedio3X: r3.intervaloMedioFmt,
      proximoHorario10X: r10.proximoHorario,
      intervaloMedio10X: r10.intervaloMedioFmt,
    };
  };

  /* ---------- Buscar histórico ---------- */
  const buscarHistorico = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const params = { date: dateNow, numberVelas, betHouse: "MINES_BET" };
      const { data } = await axios.get(
        "https://api-aviator-new-e377b0de89f5.herokuapp.com/history-odd",
        { params }
      );

      setHistorico(data || []);
      const res = analisarHistorico(data || []);
      setSinal(res);
      setLastRun(moment().format("HH:mm:ss"));
    } catch (err) {
      console.error("Erro ao buscar histórico:", err);
      setErrorMsg("Não foi possível analisar agora. Tente novamente em instantes.");
    } finally {
      setLoading(false);
    }
  };

  /* ---------- Inicia o jogo ---------- */
  const startCasinoGame = async () => {
    const isMobileDevice = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ? 1 : 0;
    const gameId = "spribe/aviator";

    try {
      setUrlAviator("");
      const response = await cactusApi.startGame({
        slug: gameId,
        platform: isMobileDevice ? "MOBILE" : "WEB",
        use_demo: 0
      });

      setUrlAviator(response.data.game_url || "");
    } catch (error) {
      console.error("Erro ao iniciar o jogo de cassino:", error);
      alert("Sua sessão expirou ou é inválida. Por favor, faça login novamente.");
    }
  };

  useEffect(() => { startCasinoGame(); }, []);

  // Auto refresh simples (60s)
  useEffect(() => {
    if (!autoRefresh) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    buscarHistorico(); // dispara ao ativar
    intervalRef.current = setInterval(() => buscarHistorico(), 60000);
    return () => clearInterval(intervalRef.current);
  }, [autoRefresh, numberVelas]);

  /* ---------- Preparar strip de últimos multiplicadores ---------- */
  const ultimosMultiplicadores = useMemo(() => {
    if (!historico || historico.length === 0) return [];

    // ordena por data/hora (mais antigo → mais recente)
    const sorted = [...historico].sort((a, b) => {
      const da = a?.date || (a?.formattedDate && a?.hour ? `${a.formattedDate} ${a.hour}` : "");
      const db = b?.date || (b?.formattedDate && b?.hour ? `${b.formattedDate} ${b.hour}` : "");
      if (!da && !db) return 0;
      if (!da) return -1;
      if (!db) return 1;
      return da.localeCompare(db);
    });

    // pega os 30 mais recentes e inverte para mais recente à ESQUERDA
    return sorted.slice(-30).reverse();
  }, [historico]);

  // Auto-scroll para a ESQUERDA após carregar/atualizar
  useEffect(() => {
    if (stripRef.current && !loading) {
      stripRef.current.scrollLeft = 0;
    }
  }, [ultimosMultiplicadores, loading]);

  return (
    <div className="content aviator-pro" style={{ color: COLORS.text }}>
      <HeaderBar onBack={() => history.push("/admin/signals")} />

      {/* Controles */}
      <Card className="mb-3" style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
        <CardBody className="py-3">
          <Form onSubmit={(e) => { e.preventDefault(); buscarHistorico(); }}>
            <Row className="align-items-end">
              <Col xs={6} md={3}>
                <FormGroup className="mb-2">
                  <Label style={{ color: COLORS.sub }}>Nº de velas</Label>
                  <Input
                    type="number"
                    min="50"
                    max="300"
                    step="10"
                    value={numberVelas}
                    disabled
                    onChange={(e) => setNumberVelas(Number(e.target.value))}
                    style={{ background: COLORS.bg, color: COLORS.text, border: `1px solid ${COLORS.border}` }}
                  />
                </FormGroup>
              </Col>

              <Col xs={6} md={3}>
                <div className="custom-control custom-switch mt-4">
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    id="autoRefreshSwitch"
                    checked={autoRefresh}
                    onChange={() => setAutoRefresh(!autoRefresh)}
                  />
                  <label
                    className="custom-control-label"
                    htmlFor="autoRefreshSwitch"
                    style={{ color: COLORS.sub, cursor: "pointer" }}
                  >
                    Auto-atualizar (60s)
                  </label>
                </div>
              </Col>

              <Col xs={12} md="auto" className="ml-md-auto">
                <Button
                  color="primary"
                  className="w-100"
                  onClick={buscarHistorico}
                  disabled={loading}
                  style={{ minWidth: 190, background: COLORS.primary, border: "none" }}
                >
                  {loading ? (
                    <span className="d-inline-flex align-items-center">
                      <TailSpin height={18} width={18} color="#fff" ariaLabel="loading" />
                      <span className="ml-2">Analisando…</span>
                    </span>
                  ) : "Analisar horários"}
                </Button>
              </Col>
            </Row>

            {/* Última análise (shimmer quando carrega) */}
            <div className="mt-2" style={{ color: COLORS.sub, fontSize: 12 }}>
              {loading ? (
                <Shimmer width={160} height={14} />
              ) : lastRun ? (
                <>Última análise: <strong style={{ color: COLORS.text }}>{lastRun}</strong></>
              ) : (
                "—"
              )}
            </div>
          </Form>
        </CardBody>
      </Card>

      {errorMsg && (
        <Alert color="danger" style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
          {errorMsg}
        </Alert>
      )}

      {/* Área principal */}
      <Row className="mt-3">
        {/* Coluna das métricas */}
        <Col xs="12" lg="5">
          {/* Strip de últimos multiplicadores */}
          <MultipliersStrip data={ultimosMultiplicadores} loading={loading} containerRef={stripRef} />

          <StatCard
            title={<span>Velas <strong style={{ color: COLORS.yellow }}>2x–3x</strong></span>}
            accent={COLORS.primary}
            nextTime={sinal?.proximoHorario3X}
            intervalText={sinal?.intervaloMedio3X ? `A cada ${sinal.intervaloMedio3X} sai uma 3x+` : ""}
            loading={loading}
          />

          <StatCard
            title={<span>Velas <strong style={{ color: COLORS.yellow }}>5x–10x</strong></span>}
            accent={COLORS.pink}
            nextTime={sinal?.proximoHorario10X}
            intervalText={sinal?.intervaloMedio10X ? `A cada ${sinal.intervaloMedio10X} sai uma 10x` : ""}
            loading={loading}
          />

          {/* Amostra analisada (shimmer enquanto carrega) */}
          <Card style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
            <CardBody className="py-2">
              <div style={{ color: COLORS.sub, fontSize: 12 }}>
                {loading ? (
                  <Shimmer width={220} height={14} />
                ) : historico?.length > 0 ? (
                  <>Amostra analisada: <strong style={{ color: COLORS.text }}>{historico.length}</strong> entradas.</>
                ) : (
                  "Amostra analisada: —"
                )}
              </div>
            </CardBody>
          </Card>
        </Col>

        {/* Coluna do jogo */}
        <Col xs="12" lg="7" className="mt-4 mt-lg-0">
          <Card style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
            <CardBody className="py-3">
              <AviatorFrame title="Aviator" src={handleIframeUrl()} />
            </CardBody>
            {urlAviator === "" && (
              <CardFooter style={{ color: COLORS.sub, fontSize: 12, borderTop: `1px solid ${COLORS.border}` }}>
                Carregando jogo…
              </CardFooter>
            )}
          </Card>
        </Col>
      </Row>

      {/* Componente FloatingClock */}
      <FloatingClock 
        isVisible={showFloatingClock} 
        onClose={() => setShowFloatingClock(false)} 
      />
    </div>
  );
};

export default AviatorPRO;
